import React from 'react'
import {
  Auth0Client,
  GetTokenSilentlyOptions,
  GetTokenSilentlyVerboseResponse,
  User
} from '@auth0/auth0-spa-js'
import {SocialConnections} from '../modules/thunks'
import {USER_TIERS} from '../../offer/business/privateDeals'
import {MessageChannelsType} from './Passwordless'

export {User} from '@auth0/auth0-spa-js'
import {User as CognitoUser} from './Cognito'

export type AppMetaData = {
  tier?: USER_TIERS[]
  lastCheckoutDate?: string
}

export type ClaimsType = {
  // Domain is dynamic, we only look at logins_count
  'http://findhotel.net/logins_count': number
  'http://findhotel.net/app_metadata': AppMetaData
  __raw: string
  aud: string
  email: string
  email_verified: boolean
  exp: number
  iat: number
  iss: string
  name: string
  nickname: string
  nonce: string
  picture: string
  sub: string
  updated_at: string
  'https://findhotel.net/user_id': string
  'https://findhotel.net/createdAt': string
}

/**
 * Source component enum for old event logging (which component users clicked on login/signup button)
 * Should be removed once all events are migrated
 * @deprecated - use SourceComponentType instead
 */
export enum TrackUserSignInClickSourceComponent {
  'UserWidgetSignIn' = 'UserWidgetSignIn',
  'UserWidgetSignUp' = 'UserWidgetSignUp',
  'LockedDeal' = 'LockedDeal',
  'ActivatePlusMembership' = 'ActivatePlusMembership',
  'UnauthorizedBookingErrorMonitor' = 'UnauthorizedBookingErrorMonitor',
  'RoomSelectionCard' = 'RoomSelectionCard',
  'UserPanelSignIn' = 'UserPanelSignIn',
  'UserPanelSignUp' = 'UserPanelSignUp',
  'PasswordlessLogin' = 'PasswordlessLogin',
  'LoginWithGoogle' = 'LoginWithGoogle',
  'SignUpWithGoogle' = 'SignUpWithGoogle',
  'LoginWithFacebook' = 'LoginWithFacebook',
  'SignUpWithFacebook' = 'SignUpWithFacebook',
  'SignUpFooter' = 'SignUpFooter',
  'LoginFooter' = 'LoginFooter',
  'MembershipPage' = 'MembershipPage',
  'AccountSideBar' = 'AccountSideBar',
  'QueryString' = 'QueryString'
}

/**
 * Source component enum for which component users clicked on login/signup button
 */
export enum SourceComponentType {
  Signup = 'Signup',
  Login = 'Login',
  UnlockOverlay = 'UnlockOverlay',
  Header = 'Header',
  PreferencesOverlay = 'PreferencesOverlay',
  PreferencesOverlayFooter = 'PreferencesOverlayFooter',
  UnlockOverlayFooter = 'UnlockOverlayFooter',
  MembershipActivate = 'MembershipActivate',
  MembershipPage = 'MembershipPage',
  AccountSideBar = 'AccountSideBar',
  CongratulationsOverlay = 'CongratulationsOverlay',
  LoginBanner = 'LoginBanner',

  // Those might be deprecated, if they are not being logged they should be removed
  UnauthorizedBookingErrorMonitor = 'UnauthorizedBookingErrorMonitor',
  RoomSelectionCard = 'RoomSelectionCard'
}

/**
 * Different types of social login options
 */
export enum SocialAuthenticationMethod {
  Google = 'Google',
  Facebook = 'Facebook'
}

export type AuthConnection = {
  domain: string
  clientId: string
}

export interface AuthConnectionWithAudience extends AuthConnection {
  audience: string
}

/**
 * Enumerable that holds the possible authentication flows an user can go through
 */
export enum AuthenticationFlows {
  UnlockOverlay = 'unlockOverlay',
  SignUp = 'signUp',
  Login = 'login'
}

/**
 * User last connection method
 */
export type UserLastConnectionMethod = MessageChannelsType | SocialConnections

/**
 * Passwordless login flow necessary params
 */
export type ShowPasswordlessLoginPayload = {
  anonymousId: string
  flow: AuthenticationFlows
  sourceComponent: string
}

export type AuthContextType = {
  authEnabled: boolean
  getIdTokenClaims?: Auth0Client['getIdTokenClaims']
  getTokenSilently?: (
    options: GetTokenSilentlyOptions
  ) => Promise<GetTokenSilentlyVerboseResponse> | Promise<string>
  isAuthenticated?: boolean
  isMemberPlus?: boolean
  isEmployee?: boolean
  lastCheckoutDate?: string
  isPrivileged?: boolean
  isAudience?: boolean
  isLoading?: boolean
  logIn?: (arg0: ShowPasswordlessLoginPayload) => void
  logOut?: () => void
  user?: User
  accountUrl?: string
  setUser?: React.Dispatch<React.SetStateAction<User>>
  authConnection?: AuthConnectionWithAudience
  loginPathName?: string
}

export type AuthenticatedUser = {
  isAuthenticated: boolean
}

export type AudienceToken = {
  isAudience: boolean
}

export type UserAction = {
  user: CognitoUser
  isAuthenticated: boolean
  isPrivileged: boolean
}
